/* ════════════════════════════════════════════════════════════════════════
   HOME OS · MOCK DATA — single source of truth
   ────────────────────────────────────────────────────────────────────────
   Every Home OS page reads from this file. Each exported object includes
   the canonical (spec) field names plus a small set of legacy aliases so
   existing components keep rendering without redesign.

   Replace any value in this file → it shows up everywhere automatically.
   Future backend wiring: swap each export with an API loader hook.
   ════════════════════════════════════════════════════════════════════════ */

import {
  Sun, Clock, Users, UtensilsCrossed, ListChecks, CalendarDays,
  Wallet, ShoppingCart, Heart, FileText, Sparkles, Zap, Moon,
  ShieldCheck, Pause,
} from "lucide-react";

/* ────────────────────────────────────────────────────────────────────────
   1. HOUSEHOLD MEMBERS
   Canonical fields per spec + legacy aliases for FamilyMemberCard*:
   color, tasks, energy, note, routineStatus, profile (rich profile tabs).
   ──────────────────────────────────────────────────────────────────────── */

export const householdMembers = [
  {
    id: "mum",
    name: "Sarah",
    role: "Household lead",
    ageGroup: "Adult",
    avatarIcon: "user-round",
    energyStatus: "Medium",
    tasksToday: 7,
    nextResponsibility: "Approve dinner plan",
    routineStatus: "5 of 8 done",
    notes: "Carrying high mental load",
    preferences: ["No shellfish", "Prefers vegetarian dinners on weeknights", "Coffee — oat milk"],
    supportNeeds: ["Suggest Calm Mode after 8 PM", "Delegate dinner planning when load is high"],
    responsibilities: ["Meal planning", "School admin", "Doctor appointments", "Household shopping", "Evening routines"],

    // legacy aliases for FamilyMemberCard*
    energy: "Medium",
    tasks: 7,
    color: "#7c3aed",
    note: "Carrying high mental load",
    profile: {
      dailyRoutine: [
        { time: "6:30 AM", task: "Wake up, coffee, review today", done: true },
        { time: "7:00 AM", task: "School lunch prep", done: true },
        { time: "7:30 AM", task: "Help kids get ready", done: true },
        { time: "9:00 AM", task: "Work — remote standup", done: true },
        { time: "3:30 PM", task: "After-school snacks + homework help", done: false },
        { time: "5:30 PM", task: "Dinner prep", done: false },
        { time: "7:30 PM", task: "Kids bedtime routine", done: false },
        { time: "9:00 PM", task: "Personal wind-down", done: false },
      ],
      responsibilities: ["Meal planning", "School admin", "Doctor appointments", "Household shopping", "Evening routines"],
      foodPreferences: ["No shellfish", "Prefers vegetarian dinners on weeknights", "Coffee — oat milk"],
      healthNotes: ["GP check-up — next Thursday", "Feeling tired this week — low iron possible", "Multivitamin daily"],
      schoolWorkNotes: ["Works from home Mon–Thu", "In office Fridays", "Available for school pickups on flex days"],
      aiSuggestions: ["Mental load is high — consider delegating dinner planning to Dad this week", "Suggest Calm Mode after 8 PM to protect wind-down time"],
    },
  },
  {
    id: "dad",
    name: "Brad",
    role: "Transport + repairs",
    ageGroup: "Adult",
    avatarIcon: "user-round",
    energyStatus: "Good",
    tasksToday: 3,
    nextResponsibility: "School pickup",
    routineStatus: "2 of 5 done",
    notes: "Closest to town today",
    preferences: ["Eats everything", "Likes BBQ and grilled food", "Enjoys cooking on weekends"],
    supportNeeds: ["Reminder for daily BP medication"],
    responsibilities: ["School drop-off & pickup", "Car maintenance", "Home repairs", "Weekend groceries", "Bins & recycling"],

    energy: "Good",
    tasks: 3,
    color: "#1a4d3e",
    note: "Closest to town today",
    profile: {
      dailyRoutine: [
        { time: "6:15 AM", task: "Wake up, gym or walk", done: true },
        { time: "7:30 AM", task: "School drop-off", done: true },
        { time: "8:30 AM", task: "Work — on site or calls", done: false },
        { time: "3:10 PM", task: "School pickup", done: false },
        { time: "6:30 PM", task: "Dinner + family time", done: false },
      ],
      responsibilities: ["School drop-off & pickup", "Car maintenance", "Home repairs", "Weekend groceries", "Bins & recycling"],
      foodPreferences: ["Eats everything", "Likes BBQ and grilled food", "Enjoys cooking on weekends"],
      healthNotes: ["Blood pressure medication — daily morning", "Next GP check — 3 months", "Good energy this week"],
      schoolWorkNotes: ["Works in town Mon–Fri", "Flexible 3 PM finish for school runs", "Closest to school for pickups"],
      aiSuggestions: ["Brad has capacity — consider assigning dinner prep tonight", "Car service due Thursday — book early slot to avoid school run clash"],
    },
  },
  {
    id: "teen",
    name: "Lily",
    role: "School + chores",
    ageGroup: "Teen",
    avatarIcon: "user-round",
    energyStatus: "Medium",
    tasksToday: 4,
    nextResponsibility: "Pack lunch containers",
    routineStatus: "3 of 6 done",
    notes: "Gym after school",
    preferences: ["Vegetarian at school", "Likes wraps and sushi", "No mushrooms", "Smoothies for breakfast"],
    supportNeeds: ["Keep chores light on gym days"],
    responsibilities: ["Empty dishwasher (daily)", "Wipe kitchen bench (daily)", "Pack own lunch", "Help with younger siblings"],

    energy: "Medium",
    tasks: 4,
    color: "#ec4899",
    note: "Gym after school",
    profile: {
      dailyRoutine: [
        { time: "6:45 AM", task: "Wake up, get dressed", done: true },
        { time: "7:15 AM", task: "Breakfast + pack bag", done: true },
        { time: "8:15 AM", task: "School", done: true },
        { time: "3:30 PM", task: "After-school — gym / netball", done: false },
        { time: "5:00 PM", task: "Homework", done: false },
        { time: "7:00 PM", task: "Help with dishes + pack lunch containers", done: false },
      ],
      responsibilities: ["Empty dishwasher (daily)", "Wipe kitchen bench (daily)", "Pack own lunch", "Help with younger siblings"],
      foodPreferences: ["Vegetarian at school", "Likes wraps and sushi", "No mushrooms", "Smoothies for breakfast"],
      healthNotes: ["Retainer every night", "Netball practice Tue & Thu", "Game Saturday 9 AM"],
      schoolWorkNotes: ["Year 11", "Art project due next week", "Parent-teacher conference tomorrow evening"],
      aiSuggestions: ["Lily has gym today — suggest easy chores only", "Art supplies needed by Thursday — add to shopping list"],
    },
  },
  {
    id: "child",
    name: "Max",
    role: "School routine",
    ageGroup: "Child",
    avatarIcon: "user-round",
    energyStatus: "Low",
    tasksToday: 2,
    nextResponsibility: "Shoes and bag",
    routineStatus: "1 of 4 done",
    notes: "Needs visual routine",
    preferences: ["No peanuts — allergy", "Likes pasta and rice", "Prefers plain food", "Apple juice not orange"],
    supportNeeds: ["Visual checklists", "Low task count on tired days", "EpiPen in school bag"],
    responsibilities: ["Feed the cat (daily)", "Take bins out (Tue & Fri)", "Keep room tidy", "Sort recycling (weekly)"],

    energy: "Low",
    tasks: 2,
    color: "#f59e0b",
    note: "Needs visual routine",
    profile: {
      dailyRoutine: [
        { time: "6:45 AM", task: "Wake up, get dressed (visual chart)", done: true },
        { time: "7:15 AM", task: "Breakfast + brush teeth", done: false },
        { time: "8:15 AM", task: "School — shoes + bag check", done: false },
        { time: "7:00 PM", task: "Reading time + bed prep", done: false },
      ],
      responsibilities: ["Feed the cat (daily)", "Take bins out (Tue & Fri)", "Keep room tidy", "Sort recycling (weekly)"],
      foodPreferences: ["No peanuts — allergy", "Likes pasta and rice", "Prefers plain food", "Apple juice not orange"],
      healthNotes: ["Peanut allergy — EpiPen in school bag", "Dentist tomorrow 8:30 AM", "Had a cough last week — improving", "Vitamin D daily"],
      schoolWorkNotes: ["Year 5", "School trip permission slip due Thursday", "Play date Friday 3:30 PM", "Responds well to visual prompts and checklists"],
      aiSuggestions: ["Max has low energy — keep tasks simple and visual today", "Dentist tomorrow — set reminder for Dad at 8 AM"],
    },
  },
  {
    id: "baby",
    name: "Rosie",
    role: "Care routine",
    ageGroup: "Baby",
    avatarIcon: "baby",
    energyStatus: "Medium",
    tasksToday: 0,
    nextResponsibility: "Nap time",
    routineStatus: "On track",
    notes: "Nappies running low",
    preferences: ["Lactose-free formula", "Starting solids — purees", "No honey"],
    supportNeeds: ["Teething comfort", "Quiet nap window 1:30–3:00 PM"],
    responsibilities: [],

    energy: "Medium",
    tasks: 0,
    color: "#06b6d4",
    note: "Nappies running low",
    profile: {
      dailyRoutine: [
        { time: "6:30 AM", task: "Wake + morning feed", done: true },
        { time: "9:00 AM", task: "Morning nap", done: true },
        { time: "12:00 PM", task: "Lunch + play", done: false },
        { time: "1:30 PM", task: "Afternoon nap", done: false },
        { time: "5:00 PM", task: "Bath + evening routine", done: false },
        { time: "6:30 PM", task: "Bedtime feed + sleep", done: false },
      ],
      responsibilities: [],
      foodPreferences: ["Lactose-free formula", "Starting solids — purees", "No honey"],
      healthNotes: ["Teething — unsettled some nights", "6-month check-up done", "Immunisations up to date"],
      schoolWorkNotes: [],
      aiSuggestions: ["Nappies running low — add to shopping list", "Rosie teething — expect some disrupted naps today"],
    },
  },
];

/* ────────────────────────────────────────────────────────────────────────
   2. HOUSEHOLD TASKS
   ──────────────────────────────────────────────────────────────────────── */

export const TASK_CATEGORIES = [
  "Meals", "Chores", "Calendar", "Money", "Supplies",
  "Health", "Documents", "Transport", "School", "Family", "Admin",
];

export const TASK_STATUSES = [
  "Upcoming", "Done", "Needs action", "Paused", "Ara suggested",
];

export const householdTasks = [
  {
    id: "t-001",
    title: "Defrost chicken mince",
    description: "For tonight's dinner — taco bowls.",
    category: "Meals",
    assignedTo: "dad",
    dueDate: "2026-02-06",
    dueTime: "15:00",
    recurrence: null,
    status: "Needs action",
    priority: "high",
    energyRequired: "low",
    ageSuitability: ["Adult"],
    aiSuggested: true,
    approvalRequired: false,
    source: "meal plan",
    relatedPerson: "dad",
    relatedFile: null,
    notes: "Pull from freezer by 3 PM.",
  },
  {
    id: "t-002",
    title: "Empty dishwasher + wipe bench",
    description: "Daily kitchen reset.",
    category: "Chores",
    assignedTo: "teen",
    dueDate: "2026-02-06",
    dueTime: "16:30",
    recurrence: "daily",
    status: "Done",
    priority: "medium",
    energyRequired: "low",
    ageSuitability: ["Teen", "Adult"],
    aiSuggested: false,
    approvalRequired: false,
    source: "chore rotation",
    relatedPerson: "teen",
    relatedFile: null,
    notes: "",
  },
  {
    id: "t-003",
    title: "School pickup",
    description: "Pick Max + Lily up from the school gate.",
    category: "Calendar",
    assignedTo: "dad",
    dueDate: "2026-02-06",
    dueTime: "15:10",
    recurrence: "weekday",
    status: "Upcoming",
    priority: "high",
    energyRequired: "low",
    ageSuitability: ["Adult"],
    aiSuggested: false,
    approvalRequired: false,
    source: "school calendar",
    relatedPerson: "dad",
    relatedFile: null,
    notes: "",
  },
  {
    id: "t-004",
    title: "Confirm Lily's camp deposit",
    description: "$120 due to school office by next Mon.",
    category: "Money",
    assignedTo: "mum",
    dueDate: "2026-02-09",
    dueTime: null,
    recurrence: null,
    status: "Needs action",
    priority: "high",
    energyRequired: "low",
    ageSuitability: ["Adult"],
    aiSuggested: false,
    approvalRequired: true,
    source: "school newsletter",
    relatedPerson: "teen",
    relatedFile: "school-newsletter-feb.pdf",
    notes: "",
  },
  {
    id: "t-005",
    title: "Add nappies to shopping list",
    description: "Running low.",
    category: "Supplies",
    assignedTo: "ara",
    dueDate: "2026-02-06",
    dueTime: null,
    recurrence: null,
    status: "Ara suggested",
    priority: "medium",
    energyRequired: "low",
    ageSuitability: ["Adult"],
    aiSuggested: true,
    approvalRequired: false,
    source: "pantry check",
    relatedPerson: "baby",
    relatedFile: null,
    notes: "",
  },
  {
    id: "t-006",
    title: "Max — dentist appointment",
    description: "8:30 AM checkup.",
    category: "Health",
    assignedTo: "dad",
    dueDate: "2026-02-07",
    dueTime: "08:30",
    recurrence: null,
    status: "Upcoming",
    priority: "high",
    energyRequired: "low",
    ageSuitability: ["Adult"],
    aiSuggested: false,
    approvalRequired: true,
    source: "calendar",
    relatedPerson: "child",
    relatedFile: null,
    notes: "Confirm appointment tonight.",
  },
  {
    id: "t-007",
    title: "Return school trip permission slip",
    description: "Year 5 trip — sign and return to Max's teacher.",
    category: "School",
    assignedTo: "mum",
    dueDate: "2026-02-08",
    dueTime: null,
    recurrence: null,
    status: "Needs action",
    priority: "high",
    energyRequired: "low",
    ageSuitability: ["Adult"],
    aiSuggested: false,
    approvalRequired: true,
    source: "school newsletter",
    relatedPerson: "child",
    relatedFile: "school-newsletter-feb.pdf",
    notes: "",
  },
  {
    id: "t-008",
    title: "Book parent-teacher conference slot",
    description: "Slots open online.",
    category: "School",
    assignedTo: "mum",
    dueDate: "2026-02-11",
    dueTime: null,
    recurrence: null,
    status: "Needs action",
    priority: "medium",
    energyRequired: "low",
    ageSuitability: ["Adult"],
    aiSuggested: false,
    approvalRequired: true,
    source: "school newsletter",
    relatedPerson: "teen",
    relatedFile: null,
    notes: "",
  },
  {
    id: "t-009",
    title: "Car service",
    description: "Mechanic booked Thursday all day — $350 est.",
    category: "Transport",
    assignedTo: "dad",
    dueDate: "2026-02-10",
    dueTime: null,
    recurrence: null,
    status: "Upcoming",
    priority: "medium",
    energyRequired: "medium",
    ageSuitability: ["Adult"],
    aiSuggested: false,
    approvalRequired: false,
    source: "calendar",
    relatedPerson: "dad",
    relatedFile: null,
    notes: "",
  },
  {
    id: "t-010",
    title: "Family reset — short check-in",
    description: "20 mins as a family to review the week.",
    category: "Family",
    assignedTo: "all",
    dueDate: "2026-02-06",
    dueTime: "19:00",
    recurrence: "weekly",
    status: "Ara suggested",
    priority: "low",
    energyRequired: "low",
    ageSuitability: ["Adult", "Teen", "Child"],
    aiSuggested: true,
    approvalRequired: false,
    source: "ara",
    relatedPerson: null,
    relatedFile: null,
    notes: "",
  },
  {
    id: "t-011",
    title: "Renew passports — kids",
    description: "Both kids' passports expire in 6 months.",
    category: "Documents",
    assignedTo: "mum",
    dueDate: "2026-02-28",
    dueTime: null,
    recurrence: null,
    status: "Paused",
    priority: "low",
    energyRequired: "medium",
    ageSuitability: ["Adult"],
    aiSuggested: false,
    approvalRequired: true,
    source: "documents review",
    relatedPerson: null,
    relatedFile: null,
    notes: "Push to next week — busy this week.",
  },
  {
    id: "t-012",
    title: "Pay power bill",
    description: "$142 due Friday.",
    category: "Money",
    assignedTo: "dad",
    dueDate: "2026-02-09",
    dueTime: null,
    recurrence: "monthly",
    status: "Upcoming",
    priority: "high",
    energyRequired: "low",
    ageSuitability: ["Adult"],
    aiSuggested: false,
    approvalRequired: false,
    source: "recurring bill",
    relatedPerson: null,
    relatedFile: null,
    notes: "",
  },
];

/* ────────────────────────────────────────────────────────────────────────
   3. TODAY TIMELINE
   ──────────────────────────────────────────────────────────────────────── */

export const todayTimeline = [
  { id: 1, time: "7:30 AM",  title: "School drop-off prep",      assignedTo: "Mum",      category: "Calendar", status: "done",         aiSuggested: false },
  { id: 2, time: "8:15 AM",  title: "Leave for school",          assignedTo: "Dad",      category: "Transport", status: "done",         aiSuggested: false },
  { id: 3, time: "10:00 AM", title: "Grocery order reminder",    assignedTo: "Mum",      category: "Supplies",  status: "needs_action", aiSuggested: false },
  { id: 4, time: "3:10 PM",  title: "School pickup",             assignedTo: "Dad",      category: "Calendar",  status: "upcoming",     aiSuggested: false },
  { id: 5, time: "5:30 PM",  title: "Dinner prep",               assignedTo: "Mum",      category: "Meals",     status: "upcoming",     aiSuggested: false },
  { id: 6, time: "7:00 PM",  title: "Family reset",              assignedTo: "Everyone", category: "Family",    status: "ai_suggested", aiSuggested: true  },
  { id: 7, time: "8:30 PM",  title: "Wind-down routine",         assignedTo: "Everyone", category: "Family",    status: "upcoming",     aiSuggested: false },
];

/* ────────────────────────────────────────────────────────────────────────
   4. AI SUGGESTIONS (cross-module nudges shown on the dashboard)
   ──────────────────────────────────────────────────────────────────────── */

export const aiSuggestions = [
  {
    id: 1,
    title: "Move grocery order earlier",
    description: "Move grocery order to today before 12 PM.",
    category: "Supplies",
    priority: "medium",
    suggestedAction: "Reschedule grocery order",
    approvalRequired: false,
    icon: Clock, // legacy alias for HomeOSPage
  },
  {
    id: 2,
    title: "Lighten Mum's load tonight",
    description: "Assign school lunch prep to Teen tonight.",
    category: "Family",
    priority: "medium",
    suggestedAction: "Reassign task to Lily",
    approvalRequired: false,
    icon: Users,
  },
  {
    id: 3,
    title: "Pick a quick dinner",
    description: "Use a quick dinner because today has a busy evening.",
    category: "Meals",
    priority: "high",
    suggestedAction: "Swap dinner plan",
    approvalRequired: false,
    icon: Zap,
  },
  {
    id: 4,
    title: "Consider Calm Mode",
    description: "Turn on Calm Mode if the household feels overloaded.",
    category: "Family",
    priority: "low",
    suggestedAction: "Activate Calm Mode",
    approvalRequired: false,
    icon: Moon,
  },
];

/* ────────────────────────────────────────────────────────────────────────
   5. HOME MODULES (cards on the dashboard)
   ──────────────────────────────────────────────────────────────────────── */

export const homeModules = [
  { id: "family",    title: "Family",             description: "Members, roles & energy",      route: "/home-os/family",         iconName: "users",          icon: Users,           statusText: "5 members",      count: 5,  colourTone: "pink",     color: "bg-pink-50 border-pink-100 dark:bg-pink-900/15 dark:border-pink-800",       label: "Family",             desc: "Members, roles & energy",      status: "5 members",      href: "/home-os/family" },
  { id: "meals",     title: "Meals",              description: "Meal plan & grocery lists",    route: "/home-os/meals",          iconName: "utensils",       icon: UtensilsCrossed, statusText: "7 days planned", count: 7,  colourTone: "emerald",  color: "bg-emerald-50 border-emerald-100 dark:bg-emerald-900/15 dark:border-emerald-800", label: "Meals",            desc: "Meal plan & grocery lists",    status: "7 days planned", href: "/home-os/meals" },
  { id: "chores",    title: "Chores",             description: "Tasks, rotation & streaks",    route: "/home-os/chores",         iconName: "list-checks",    icon: ListChecks,      statusText: "6 open",         count: 6,  colourTone: "amber",    color: "bg-amber-50 border-amber-100 dark:bg-amber-900/15 dark:border-amber-800",   label: "Chores",             desc: "Tasks, rotation & streaks",    status: "6 open",         href: "/home-os/chores" },
  { id: "calendar",  title: "Calendar",           description: "Events, school & activities",  route: "/home-os/calendar",       iconName: "calendar",       icon: CalendarDays,    statusText: "9 this week",    count: 9,  colourTone: "blue",     color: "bg-blue-50 border-blue-100 dark:bg-blue-900/15 dark:border-blue-800",       label: "Calendar",           desc: "Events, school & activities",  status: "9 this week",    href: "/home-os/calendar" },
  { id: "money",     title: "Money",              description: "Budgets, bills & savings",     route: "/home-os/money",          iconName: "wallet",         icon: Wallet,          statusText: "On track",       count: 0,  colourTone: "green",    color: "bg-green-50 border-green-100 dark:bg-green-900/15 dark:border-green-800",   label: "Money",              desc: "Budgets, bills & savings",     status: "On track",       href: "/home-os/money" },
  { id: "supplies",  title: "Supplies",           description: "Shopping & household items",   route: "/home-os/supplies",       iconName: "shopping-cart",  icon: ShoppingCart,    statusText: "11 to get",      count: 11, colourTone: "red",      color: "bg-red-50 border-red-100 dark:bg-red-900/15 dark:border-red-800",           label: "Supplies",           desc: "Shopping & household items",   status: "11 to get",      href: "/home-os/supplies" },
  { id: "health",    title: "Health",             description: "Routines, meds & check-ups",   route: "/home-os/health",         iconName: "heart",          icon: Heart,           statusText: "1 upcoming",     count: 1,  colourTone: "cyan",     color: "bg-cyan-50 border-cyan-100 dark:bg-cyan-900/15 dark:border-cyan-800",       label: "Health",             desc: "Routines, meds & check-ups",   status: "1 upcoming",     href: "/home-os/health" },
  { id: "documents", title: "Documents",          description: "School forms, insurance, IDs", route: "/home-os/documents",      iconName: "file-text",      icon: FileText,        statusText: "1 needs update", count: 1,  colourTone: "violet",   color: "bg-violet-50 border-violet-100 dark:bg-violet-900/15 dark:border-violet-800", label: "Documents",        desc: "School forms, insurance, IDs", status: "1 needs update", href: "/home-os/documents" },
  { id: "ai",        title: "AI Command Centre",  description: "Ask, suggest & automate",      route: "/home-os/command-centre", iconName: "sparkles",       icon: Sparkles,        statusText: "Live",           count: 0,  colourTone: "fuchsia",  color: "bg-fuchsia-50 border-fuchsia-100 dark:bg-fuchsia-900/15 dark:border-fuchsia-800", label: "AI Command Centre", desc: "Ask, suggest & automate",      status: "Live",           href: "/home-os/command-centre" },
];

/* ────────────────────────────────────────────────────────────────────────
   6. WEEKLY RESET MOCK PLAN — the 7 household sections, summarised
   ──────────────────────────────────────────────────────────────────────── */

export const weeklyResetMockPlan = {
  food: {
    headline: "Quick, repeatable meals",
    focus: [
      "Mon — mince + rice bowls",
      "Wed — fish tacos",
      "Fri — homemade pizza",
      "Weekend — slow-cook Sunday roast",
    ],
    note: "Built around what's already in the pantry.",
  },
  calendar: {
    headline: "Lock the big rocks early",
    focus: [
      "Tue — parent-teacher conference",
      "Thu — car service (all day)",
      "Sat — netball game (Lily)",
    ],
    note: "Two transport-heavy days — plan fuel and timing.",
  },
  chores: {
    headline: "Light but fair rotation",
    focus: [
      "Dishwasher → Teen (daily)",
      "Bins → Child (Tue + Fri)",
      "Bathroom scrub → Dad (Sat AM)",
      "Laundry sort → Mum (Sun PM)",
    ],
    note: "Only one deep-clean area for the whole week.",
  },
  money: {
    headline: "Stay inside the buffer",
    focus: [
      "Power bill — Fri ($142)",
      "Lily's camp deposit — Mon ($120)",
      "Subscriptions check — before payday",
    ],
    note: "Buffer protected this week.",
  },
  schoolKids: {
    headline: "Two small admin wins",
    focus: [
      "Permission slip — Max (return Thu)",
      "Mufti gold coin — both kids (Fri)",
      "Art supplies — Lily project (Thu)",
    ],
    note: "Add reminders to the family calendar.",
  },
  supplies: {
    headline: "Restock what's running low",
    focus: [
      "Nappies (Rosie)",
      "Dishwasher tablets",
      "Bin liners",
      "Cooking oil",
    ],
    note: "One online order, no extra trips.",
  },
  wellbeing: {
    headline: "Protect rest and connection",
    focus: [
      "Wind-down by 8:30 PM weeknights",
      "20-min family reset Wed evening",
      "Calm Mode if the day overflows",
      "Friday family night",
    ],
    note: "Add Calm Mode as a one-tap option.",
  },
};

/* ────────────────────────────────────────────────────────────────────────
   7. CALM MODE PLAN — keep / pause / simplify / askForSupport
   ──────────────────────────────────────────────────────────────────────── */

export const calmModePlan = {
  keep: {
    title: "Keep",
    icon: ShieldCheck,
    color: "bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-300",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    items: [
      "Feed everyone",
      "School and work essentials",
      "Medication and health routines",
      "Urgent bills or deadlines",
    ],
  },
  pause: {
    title: "Pause",
    icon: Pause,
    color: "bg-blue-50 border-blue-100 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300",
    iconColor: "text-blue-600 dark:text-blue-400",
    items: [
      "Non-urgent cleaning",
      "Extra errands",
      "Optional social plans",
      "Low-priority admin",
    ],
  },
  simplify: {
    title: "Simplify",
    icon: Sparkles,
    color: "bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300",
    iconColor: "text-amber-600 dark:text-amber-400",
    items: [
      "Easy dinner",
      "One load of laundry only if needed",
      "Basic kitchen reset",
      "Early night routine",
    ],
  },
  askForSupport: {
    title: "Ask for support",
    icon: Heart,
    color: "bg-rose-50 border-rose-100 text-rose-700 dark:bg-rose-900/20 dark:border-rose-800 dark:text-rose-300",
    iconColor: "text-rose-600 dark:text-rose-400",
    items: [
      "Share pickup",
      "Ask someone to grab groceries",
      "Move tasks to another day",
      "Send a simple update message",
    ],
  },
};

/* ── ordered list for convenient mapping ── */
export const calmModeSections = [
  calmModePlan.keep,
  calmModePlan.pause,
  calmModePlan.simplify,
  calmModePlan.askForSupport,
];

/* lucide icon used by Sun-based UIs that don't import lucide separately */
export { Sun as TodaySunIcon };
